// The app modal queue's vocabulary and the HCP-verification port seam. The
// queue itself stays app-side (pacing, seen-set, backend POST, dequeue); its
// pieces cross as hook references and resolved values on the props the app
// binds at the composition point (`src/components/utils/hcp-verification-modal-ports.ts`).
// `MODAL_PRIORITY` mirrors the app enum's numeric values; a numeric enum
// accepts them, so descriptors pass into the app queue without a cast. The
// wiring suite asserts the values agree.

import { type ModalId, type ModalViewAction } from '@heidi/schema/ml-scribe';
import { type ComponentType, createContext, type ReactNode, useContext } from 'react';

export const MODAL_PRIORITY = {
  LOW: 1,
  NORMAL: 5,
  HIGH: 10,
  CRITICAL: 20,
  HIGHEST: 30,
  SAFETY_SYSTEM: 40,
} as const;

export type ModalPriorityValue = (typeof MODAL_PRIORITY)[keyof typeof MODAL_PRIORITY];

/** Twin of the app queue's `QueuedModal` descriptor. */
export interface QueuedModalDescriptor {
  id: ModalId;
  priority: ModalPriorityValue;
  dismissible?: boolean;
  showOnlyAfterAccountCreation?: boolean;
  showOnce?: boolean;
}

/** Twin of the close-options the app subscription accepts. */
export interface QueuedModalCloseOptions {
  action?: ModalViewAction;
  increment_count?: boolean;
  hide_forever?: boolean;
  skipPacing?: boolean;
}

/** What `useQueuedModal` returns — the whole surface queue consumers use. */
export interface QueuedModalSubscription {
  isOpen: boolean;
  dismissible: boolean;
  handleClose: () => void;
  onOpenChange: (open: boolean) => void;
  closeWithOptions: (options?: QueuedModalCloseOptions) => void;
}

/**
 * A specialty as the picker and the flow hold it — the structural view of the
 * app's `ClinicianTypeInput` (`src/lib/types`) this feature reads.
 */
export interface ClinicianTypeInput {
  id: string;
  name: string;
  localized_name?: string;
  requires_hcp_verification?: boolean;
  group: { id: string; name: string };
}

/** The specialties query as the dialog reads it. */
export interface HcpVerificationSpecialtiesResult {
  data?: readonly ClinicianTypeInput[];
  isPending: boolean;
  isError: boolean;
  refetch: () => unknown;
}

/** The props the specialty picker forwards to the app's clinician-type select. */
export interface HcpVerificationClinicianTypeSelectProps {
  disabled?: boolean;
  selectedSpecialty?: ClinicianTypeInput;
  setSelectedSpecialty: (specialty: ClinicianTypeInput) => void;
  otherSpecialty?: string;
  setOtherSpecialty: (value?: string) => void;
  placeholder?: string;
}

/**
 * The app inputs the clinician (NPI) verification dialog needs, supplied by
 * the app at the composition point and published to descendants through
 * `HcpVerificationPortsContext`. Hooks cross by reference — rules of hooks
 * apply through them.
 */
export interface HcpVerificationModalPorts {
  /** The real app hook, by reference. */
  useQueuedModal: (modalId: ModalId) => QueuedModalSubscription;
  /** Hooks returning the user-triggered queue's enqueue/dequeue writers. */
  useEnqueueModal: () => (modal: QueuedModalDescriptor) => void;
  useDequeueModal: () => (modalId: ModalId) => void;
  /** Whether a consult is recording — the pin's stand-down signal. */
  useIsClinicalRecordingActive: () => boolean;
  /** Whether the launch row (HIGH priority) pinned this dialog on stage. */
  useIsPinnedByLaunchRow: () => boolean;
  /** The signed-in user's country code for the specialties request. */
  useCountryCode: () => string | undefined;
  /** The specialties list query, wrapped with `(d) => d.specialties` + country. */
  useSpecialties: (country: string | undefined) => HcpVerificationSpecialtiesResult;
  /** The app's clinician-type select (`@/components/ui/select/clinician-type-select`). */
  ClinicianTypeSelect: ComponentType<HcpVerificationClinicianTypeSelectProps>;
  /** The Other sentinel (`OTHER_SPECIALTY_ID` / `OTHER_SPECIALTY_NAME`). */
  otherSpecialty: { id: string; name: string };
  /** Terms and Privacy links, resolved at the composition point (on-prem aware). */
  legalLinks: { termsHref: string; privacyHref: string; openInNewTab: boolean };
}

export const HcpVerificationPortsContext =
  createContext<HcpVerificationModalPorts | null>(null);

/** Reads the ports inside the verification dialog; fails loudly outside it. */
export const useHcpVerificationPorts = (): HcpVerificationModalPorts => {
  const ports = useContext(HcpVerificationPortsContext);
  if (!ports) {
    throw new Error(
      '[ads/hcp-verification] ports not provided — render inside <HcpVerificationModal ports={…}>'
    );
  }
  return ports;
};

export const HcpVerificationPortsProvider = ({
  ports,
  children,
}: {
  ports: HcpVerificationModalPorts;
  children: ReactNode;
}) => (
  <HcpVerificationPortsContext.Provider value={ports}>
    {children}
  </HcpVerificationPortsContext.Provider>
);
